import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import {
  inboxRequest,
  type InboxSenderReview,
} from 'common/src/api/settlement-inbox';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { LoadingState } from '../ui';

type Decision = {
  action: string;
  email?: string;
  gmailId?: string;
  authReview?: boolean;
};
export default function InboxSenderApprovals({
  exporterId,
  contextKey,
}: {
  exporterId?: number;
  contextKey?: number;
}) {
  const { t } = useTranslation('inbox');
  const [data, setData] = useState<InboxSenderReview | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const current = ++sequence.current;
    try {
      const result = await inboxRequest<InboxSenderReview>('sender-review', {
        ...(exporterId ? { exporterId } : {}),
      });
      if (current === sequence.current) {
        setData(result);
        setError(false);
      }
    } catch {
      if (current === sequence.current) setError(true);
    }
  }, [exporterId, contextKey]);
  useEffect(() => {
    setData(null);
    setDecision(null);
    setConfirmed(false);
    void load();
    const timer = window.setInterval(() => {
      if (!document.hidden) void load();
    }, 30_000);
    return () => {
      clearInterval(timer);
      sequence.current++;
    };
  }, [load]);
  const labels: Record<string, string> = {
    accept_once: 'Aceptar solo este mensaje',
    reject_message: 'Rechazar mensaje',
    approve_sender: 'Agregar a lista de confianza',
    block_sender: 'Bloquear remitente',
    revoke_sender: 'Quitar regla',
  };
  const choose = (next: Decision) => {
    setDecision(next);
    setConfirmed(false);
  };
  const save = async () => {
    if (!decision || busy || (decision.authReview && !confirmed)) return;
    setBusy(true);
    try {
      await inboxRequest('decide-sender', {
        ...(exporterId ? { exporterId } : {}),
        decision: decision.action,
        gmailId: decision.gmailId,
        email: decision.email,
        securityConfirmed: confirmed,
      });
      setDecision(null);
      toast.success(
        t(
          'Decisión guardada. Los adjuntos aceptados se recibirán en la próxima sincronización.'
        )
      );
      await load();
    } catch {
      toast.error(
        t('No se pudo guardar. Actualiza la lista y revisa tus permisos.')
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap justify-between items-center gap-2'>
        <h3 className='text-lg font-semibold'>{t('Remitentes')}</h3>
        <Button
          size='sm'
          variant='flat'
          onPress={() => void load()}
          isDisabled={busy}
        >
          {t('Actualizar')}
        </Button>
      </div>
      <p className='text-sm text-default-500'>
        {t(
          'Los remitentes nuevos requieren aprobación para esta exportadora. Aprobar no omite la revisión del documento ni los controles de seguridad.'
        )}
      </p>
      {error ? (
        <p role='alert' className='text-danger'>
          {t('No se pudieron cargar las aprobaciones de remitentes.')}
        </p>
      ) : !data ? (
        <LoadingState />
      ) : (
        <>
          {!data.canManage && (
            <p className='text-sm text-warning-700'>
              {t(
                'Un administrador de la exportadora debe aprobar o bloquear remitentes.'
              )}
            </p>
          )}
          <section
            aria-label={t('Por autorizar')}
            className='space-y-3 rounded-xl border border-divider p-4'
          >
            <h4 className='flex items-center gap-2 font-semibold'>
              {t('Por autorizar')}
              {data.pending.length > 0 && (
                <Chip size='sm' color='warning' variant='flat'>
                  {data.pending.length} {t('mensajes pendientes')}
                </Chip>
              )}
            </h4>
            {!data.pending.length && (
              <p className='text-sm text-default-500'>
                {t('No hay remitentes pendientes de revisión.')}
              </p>
            )}
            {data.pending.map((message) => (
              <Card key={message.gmail_id} shadow='sm'>
                <CardBody className='gap-3'>
                  <div className='flex flex-wrap justify-between gap-2'>
                    <div className='min-w-0'>
                      <p className='font-medium break-all'>
                        {message.sender_email || message.sender_display}
                      </p>
                      <p className='text-sm break-words'>
                        {message.subject || t('Sin asunto')}
                      </p>
                    </div>
                    <Chip
                      size='sm'
                      color={
                        message.auth_result === 'pass' ? 'default' : 'warning'
                      }
                    >
                      {t(
                        message.auth_result === 'pass'
                          ? 'Remitente nuevo'
                          : 'Revisión de autenticación'
                      )}
                    </Chip>
                  </div>
                  <p className='text-xs text-default-500'>
                    {new Date(message.received_at).toLocaleString()}
                  </p>
                  <p className='text-sm break-words'>
                    {message.attachment_names.join(' · ')}
                  </p>
                  {message.auth_result !== 'pass' && (
                    <p className='text-sm text-warning-700'>
                      {t(
                        'No pudimos confirmar la identidad del remitente. Un reenvío puede causar esto; verifica el mensaje por otro medio antes de aceptarlo.'
                      )}
                    </p>
                  )}
                  {data.canManage && (
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        size='sm'
                        variant='flat'
                        isDisabled={busy}
                        onPress={() =>
                          choose({
                            action: 'accept_once',
                            gmailId: message.gmail_id,
                            email: message.sender_email || undefined,
                            authReview: message.auth_result !== 'pass',
                          })
                        }
                      >
                        {t('Aceptar solo este mensaje')}
                      </Button>
                      <Button
                        size='sm'
                        color='primary'
                        isDisabled={busy || !message.sender_email}
                        onPress={() =>
                          choose({
                            action: 'approve_sender',
                            gmailId: message.gmail_id,
                            email: message.sender_email!,
                            authReview: true,
                          })
                        }
                      >
                        {t('Agregar a lista de confianza')}
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        isDisabled={busy}
                        onPress={() =>
                          choose({
                            action: 'reject_message',
                            gmailId: message.gmail_id,
                          })
                        }
                      >
                        {t('Rechazar mensaje')}
                      </Button>
                      {message.sender_email && (
                        <Button
                          size='sm'
                          color='danger'
                          variant='light'
                          isDisabled={busy}
                          onPress={() =>
                            choose({
                              action: 'block_sender',
                              gmailId: message.gmail_id,
                              email: message.sender_email!,
                            })
                          }
                        >
                          {t('Bloquear remitente')}
                        </Button>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}
          </section>
          {(['approved', 'blocked'] as const).map((status) => {
            const senders = data.senders.filter(
              (sender) => sender.status === status
            );
            const title = status === 'approved' ? 'Autorizados' : 'Bloqueados';
            return (
              <section
                key={status}
                aria-label={t(title)}
                className='rounded-xl border border-divider p-4'
              >
                <h4 className='flex items-center gap-2 font-semibold'>
                  {t(title)}
                  <span className='text-sm font-normal text-default-500'>
                    ({senders.length})
                  </span>
                </h4>
                <div className='mt-3 space-y-3'>
                  {!senders.length && (
                    <p className='text-sm text-default-500'>
                      {t(
                        status === 'approved'
                          ? 'No hay remitentes autorizados.'
                          : 'No hay remitentes bloqueados.'
                      )}
                    </p>
                  )}
                  {senders.map((sender) => (
                    <div
                      key={sender.email}
                      className='flex flex-wrap justify-between items-center gap-2'
                    >
                      <span className='text-sm break-all'>{sender.email}</span>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Chip
                          size='sm'
                          color={
                            sender.status === 'approved' ? 'success' : 'danger'
                          }
                        >
                          {t(
                            sender.status === 'approved'
                              ? sender.allow_unverified
                                ? 'En lista de confianza'
                                : 'Aprobado'
                              : 'Bloqueado'
                          )}
                        </Chip>
                        {data.canManage && (
                          <>
                            <Button
                              size='sm'
                              variant='light'
                              isDisabled={busy}
                              onPress={() =>
                                choose({
                                  action: 'revoke_sender',
                                  email: sender.email,
                                })
                              }
                            >
                              {t('Quitar regla')}
                            </Button>
                            {sender.status === 'approved' && (
                              <Button
                                size='sm'
                                variant='light'
                                color='danger'
                                isDisabled={busy}
                                onPress={() =>
                                  choose({
                                    action: 'block_sender',
                                    email: sender.email,
                                  })
                                }
                              >
                                {t('Bloquear remitente')}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
      <Modal
        isOpen={!!decision}
        onClose={() => {
          if (!busy) setDecision(null);
        }}
        isDismissable={!busy}
        isKeyboardDismissDisabled={busy}
        hideCloseButton={busy}
      >
        <ModalContent>
          <ModalHeader>
            {decision ? t(labels[decision.action]) : ''}
          </ModalHeader>
          <ModalBody>
            <p className='break-all font-medium'>{decision?.email}</p>
            <p className='text-sm'>
              {t(
                'Esta decisión solo afecta a la exportadora actual y queda registrada con tu usuario.'
              )}
            </p>
            {decision?.action === 'approve_sender' && (
              <p className='text-sm'>
                {t(
                  'Se aceptarán los mensajes pendientes y futuros de esta dirección, incluso cuando no podamos verificar su autenticación. Solo para esta exportadora; no se autoriza todo el dominio.'
                )}
              </p>
            )}
            {decision?.action === 'approve_sender' && (
              <p className='text-sm text-warning-700'>
                {t(
                  'Una dirección de remitente sin verificar puede ser suplantada. Los controles del archivo y la revisión de la liquidación seguirán siendo obligatorios.'
                )}
              </p>
            )}
            {decision?.action === 'revoke_sender' && (
              <p className='text-sm'>
                {t(
                  'Los próximos mensajes volverán a requerir aprobación. Los mensajes antes bloqueados quedarán pendientes; los ya aceptados no se eliminan.'
                )}
              </p>
            )}
            {decision?.authReview && (
              <Checkbox
                isSelected={confirmed}
                onValueChange={setConfirmed}
                isDisabled={busy}
              >
                {t(
                  decision.action === 'approve_sender'
                    ? 'Confío en esta dirección y autorizo sus mensajes futuros aunque la autenticación no esté confirmada.'
                    : 'Verifiqué por otro medio este mensaje y acepto revisarlo aunque su autenticación no esté confirmada.'
                )}
              </Checkbox>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              isDisabled={busy}
              onPress={() => setDecision(null)}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              isLoading={busy}
              isDisabled={!!decision?.authReview && !confirmed}
              onPress={() => void save()}
            >
              {t('Confirmar')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  );
}
